<template>
  <div class="import-preview">
    <h3 class="mb-4">Preview Import Data</h3>

    <div v-if="localOptions.test" class="alert alert-warning" role="alert">
      <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
      <strong>Test Mode Active:</strong> Students will NOT be created. This is a preview only.
    </div>

    <div class="options-card mb-6">
      <h4 class="options-title">Import Options</h4>
      <div class="options-content">
        <div class="option-item">
          <label class="option-label">
            <input
              type="checkbox"
              v-model="localOptions.importPickupLocations"
              class="option-checkbox"
            >
            Import pickup locations
          </label>
          <p class="option-hint">Geocodes student addresses and creates pickup locations</p>
        </div>

        <div class="option-item">
          <label class="option-label">
            <input
              type="checkbox"
              v-model="localOptions.test"
              class="option-checkbox"
            >
            Test mode (no actual import)
          </label>
          <p class="option-hint">Preview the import without actually creating student accounts</p>
        </div>
      </div>
    </div>

    <div class="options-card mb-6">
      <h4 class="options-title">Session Types</h4>
      <p class="session-type-hint mb-4">Select the session types to use when creating student lesson plan requirements.</p>
      <div class="options-content session-type-selects">
        <div class="option-item">
          <label class="option-label" for="btw-session-type">Behind-the-Wheel Session Type</label>
          <select
            id="btw-session-type"
            v-model="localOptions.btwSessionTypeId"
            class="form-control"
            :class="{ 'is-invalid': !localOptions.btwSessionTypeId && showValidation }"
          >
            <option :value="null">-- Select Session Type --</option>
            <option
              v-for="sessionType in sessionTypes"
              :key="sessionType.id"
              :value="sessionType.id"
            >
              {{ sessionType.name }}
            </option>
          </select>
          <p class="option-hint">Used for behind-the-wheel driving lessons</p>
        </div>

        <div class="option-item">
          <label class="option-label" for="road-test-session-type">Road Test Session Type</label>
          <select
            id="road-test-session-type"
            v-model="localOptions.roadTestSessionTypeId"
            class="form-control"
            :class="{ 'is-invalid': !localOptions.roadTestSessionTypeId && showValidation }"
          >
            <option :value="null">-- Select Session Type --</option>
            <option
              v-for="sessionType in sessionTypes"
              :key="sessionType.id"
              :value="sessionType.id"
            >
              {{ sessionType.name }}
            </option>
          </select>
          <p class="option-hint">Used for road test appointments</p>
        </div>
      </div>
    </div>

    <h4 class="mb-2">Data Preview</h4>
    <p class="import-summary">
      <strong>{{ previewData.length }}</strong> students will be imported
      <span v-if="localOptions.importPickupLocations">
        with pickup location geocoding
      </span>
    </p>
    <p class="text-caption mb-4">
      The following shows how your data will be imported into our system.
      Review the mapped fields to ensure everything looks correct.
    </p>
    
    <div class="table-container">
      <table class="data-table">
        <thead>
          <tr>
            <th v-for="header in tableHeaders" :key="header.value">
              {{ header.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in previewData.slice(0, 5)" :key="index">
            <td v-for="header in tableHeaders" :key="header.value">
              {{ item[header.value] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <div class="mt-4 text-body-2">
      <p><strong>Note:</strong> This preview shows up to 5 rows from your data file. The actual import will process all rows.</p>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    previewData: {
      type: Array,
      required: true
    },
    options: {
      type: Object,
      default: () => ({
        importPickupLocations: true,
        test: false,
        btwSessionTypeId: null,
        roadTestSessionTypeId: null
      })
    },
    sessionTypes: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      localOptions: { ...this.options },
      showValidation: false
    }
  },
  
  computed: {
    tableHeaders() {
      if (!this.previewData.length) return [];
      
      const sample = this.previewData[0];
      return Object.keys(sample).map(key => ({
        text: this.formatFieldLabel(key),
        value: key,
        sortable: false
      }));
    }
  },
  
  watch: {
    localOptions: {
      handler(newOptions) {
        this.$emit('update:options', newOptions);
      },
      deep: true
    }
  },
  
  methods: {
    formatFieldLabel(key) {
      return key.replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim();
    },

    validateSessionTypes() {
      this.showValidation = true;
      return this.localOptions.btwSessionTypeId && this.localOptions.roadTestSessionTypeId;
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../../../assets/css/partials/variables';

.import-preview {
  max-width: 100%;
  overflow-x: auto;
}

.alert {
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;

  i {
    flex-shrink: 0;
  }
}

.alert-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

.import-summary {
  font-size: 16px;
  margin-bottom: 8px;

  strong {
    color: $green;
  }
}

.options-card {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 16px;
}

.options-title {
  font-size: 1.25rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.options-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.option-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.option-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.option-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.option-hint {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
  padding-left: 26px;
}

.session-type-hint {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.6);
  margin: 0;
}

.session-type-selects {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  .option-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .option-label {
    font-weight: 500;
    cursor: default;
  }

  .option-hint {
    padding-left: 0;
  }

  .form-control {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: $green;
      box-shadow: 0 0 0 2px rgba($green, 0.2);
    }

    &.is-invalid {
      border-color: $red;
    }
  }
}

@media (max-width: 768px) {
  .session-type-selects {
    grid-template-columns: 1fr;
  }
}

.table-container {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.data-table th {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  background-color: #f5f5f5;
  white-space: nowrap;
}

.data-table tr:last-child td {
  border-bottom: none;
}

.data-table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.mb-2 { margin-bottom: 8px; }
.mb-4 { margin-bottom: 16px; }
.mb-6 { margin-bottom: 24px; }
.mt-4 { margin-top: 16px; }
</style> 